import { InputModel } from '.';
import { SmartformFieldConfig, ElementWithMeta } from '../../types';
import { getModelPathFromIndex } from './model/getModelPath';
import { sanitizeFieldName } from './model/fieldNameSanitizer';
import { isArrayType } from './validation/isType';
import { objectUtil } from './objectUtil';

type FieldName = keyof SmartformFieldConfig;

export default function objectBuilder<Model = InputModel>(
  elements: ElementWithMeta<Model>[],
  fieldName: FieldName
): DeepPartial<Model> {
  return elements.reduce<DeepPartial<Model>>((acc, element) => {
    const mappers = element.meta.map((m) => m.initialValueMapper) ?? [];
    element.fields.forEach((field, index) => {
      const isArray = isArrayType(index, element.meta) && field[fieldName];
      const modelPath = getModelPathFromIndex(element, index);

      if (!modelPath) return;

      acc = objectUtil.setIn(
        acc,
        modelPath,
        isArray
          ? mappers[index]
            ? [mappers[index]!(field[fieldName] as string, element as ElementWithMeta)]
            : [field[fieldName]]
          : field[fieldName] && mappers[index]
            ? mappers[index]!(field[fieldName] as string, element as ElementWithMeta)
            : field[fieldName]
      );
    });
    return acc;
  }, {});
}

export function preparePristineFields<Model = InputModel>(
  initialValues: DeepPartial<Model>,
  elements: ElementWithMeta<Model>[]
) {
  let pristineInitialValues = { ...initialValues };
  elements.forEach((el) => {
    if (el.params?.forceUserInput) {
      el.fields.forEach((_f, index) => {
        const modelPath = getModelPathFromIndex(el, index);
        pristineInitialValues = objectUtil.setIn(pristineInitialValues, modelPath, undefined);
      });
    }
  });
  return pristineInitialValues;
}

export function sanitizeObjectKeys(initialValues: any): any {
  if (initialValues === null || initialValues === undefined) return initialValues;
  if (Array.isArray(initialValues)) return initialValues.map((item) => sanitizeObjectKeys(item));
  if (typeof initialValues !== 'object') return initialValues;

  const result: any = {};
  for (const key of Object.keys(initialValues)) {
    result[sanitizeFieldName(key)] = sanitizeObjectKeys(initialValues[key]);
  }
  return result;
}
